<template>
  <v-alert
    v-bind="$attrs"
    :type="type"
    border="start"
    variant="tonal"
    class="celus-alert"
    :style="{ '--celus-alert-color': `rgb(var(--v-theme-${type}))` }"
  >
    <slot />
  </v-alert>
</template>

<script setup lang="ts">
import type { PropType } from "vue";

type AlertType = "info" | "warning" | "error" | "success";

defineOptions({
  name: "CelusAlert",
  inheritAttrs: false,
});

defineProps({
  type: {
    type: String as PropType<AlertType>,
    required: true,
    validator: (value: string) =>
      ["info", "warning", "error", "success"].includes(value),
  },
});
</script>

<style scoped>
/* Vuetify's tonal variant leaves the root transparent and paints the color as a
   translucent underlay, so the page background bleeds through and shifts the
   hue on grey. An opaque surface underneath pins it to the on-white look. */
.celus-alert.v-alert--variant-tonal {
  --celus-alert-tint-opacity: 0.2;
  --celus-alert-text-mix: 60%;

  background-color: rgb(var(--v-theme-surface));
}

/* Vuetify paints the text in the undiluted type color, which only reaches ~2.5:1
   against the tinted background. Mixing in black keeps the hue while getting
   every type past the 4.5:1 AA threshold. Only the content is darkened, so the
   icon and border keep the vivid color. */
.celus-alert.v-alert--variant-tonal :deep(.v-alert__content) {
  --celus-alert-text-color: color-mix(
    in srgb,
    var(--celus-alert-color) var(--celus-alert-text-mix),
    black
  );

  color: var(--celus-alert-color);
  color: var(--celus-alert-text-color);
}

.celus-alert.v-alert--variant-tonal :deep(.v-btn.v-btn--variant-flat) {
  background-color: var(--celus-alert-color);
  color: rgb(var(--v-theme-surface));
}

/* Theme `anchor` is primary teal; it clashes on info/error/warning tints.
   Keep links in the darkened type color and underline them. */
.celus-alert.v-alert--variant-tonal :deep(.v-alert__content a) {
  color: var(--celus-alert-text-color);
  text-decoration: underline;
}

/* The underlay carries the type color as currentColor. Vuetify tints it at
   --v-activated-opacity (0.12), which reads too pale for a full-width block. */
.celus-alert.v-alert--variant-tonal :deep(.v-alert__underlay) {
  opacity: var(--celus-alert-tint-opacity);
}
</style>
